// src/components/CompareView.tsx
//
// New page — /compare. Buy-vs-build comparison for card programmes
// currently on (or considering) Marqeta's raw APIs.
//
// Two distinct comparison blocks, deliberately not merged into one
// table:
//   Block A — Marqeta auth/clearing anomaly detection. This is FDW's
//   only live, priced outcome in this space — real rows, real "Live"
//   badge, links to the actual outcome/registry.
//   Block B — DiVA-shaped use cases (BSA/AML, settlement reporting,
//   cardholder behavior monitoring, ACH fund movement). These came
//   from external Marqeta doc research this session, not from
//   anything built or verified by FDW — no outcome exists for any of
//   them. Explicitly labeled exploratory, no price, no "buy" column —
//   same ConnectActions CTA as PlatformView uses for not-yet-live
//   capability, ie "sign up / message us," not a fake waitlist.
//
// Do not add pricing or a "buy" action to Block B without an actual
// live outcome behind it — that would misrepresent build status the
// same way the false-positive-risk gap would if oversold (see
// strategy doc Section 3/8).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from './SEO';
import { Badge } from './ui/badge';
import { ConnectActions } from './ConnectActions';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from './ui/table';

type ComparisonRow = {
  dimension: string;
  build: string;
  buy: string;
};

const DETECTION_ROWS: ComparisonRow[] = [
  {
    dimension: 'What you get',
    build: 'Raw auth/clearing pairs — you write the matching logic',
    buy: 'A finished, flagged, human-reviewed finding',
  },
  {
    dimension: 'Baseline / threshold logic',
    build: 'You build and maintain thresholds per MCC',
    buy: "Built against each merchant's own historical norm",
  },
  {
    dimension: 'Who reviews a flag',
    build: 'Your team, from scratch, every time',
    buy: 'Reviewed before it reaches you, with reasoning attached',
  },
  {
    dimension: 'Engineering investment',
    build: 'Ongoing — integration, tuning, maintenance',
    buy: 'None — you receive the outcome, not the pipeline',
  },
  {
    dimension: 'Audit trail',
    build: 'You build and maintain your own logging layer',
    buy: 'Permanent, timestamped, human-attributed by default',
  },
  {
    dimension: 'Pricing model',
    build: 'Headcount / engineering time — variable, ongoing',
    buy: 'Fixed or ranged price per outcome',
  },
  {
    dimension: 'Time to value',
    build: 'Weeks to months — integration plus tuning',
    buy: 'Connects to your existing Marqeta setup',
  },
];

const DIVA_ROWS: ComparisonRow[] = [
  {
    dimension: 'BSA/AML & KYC/KYB reporting',
    build: 'Pull DiVA cardholder/demographic views, build your own reporting layer',
    buy: 'Not yet a live outcome — see below',
  },
  {
    dimension: 'Settlement & clearing reporting',
    build: 'Bulk CSV/JSON pulls, reconcile and report internally',
    buy: 'Not yet a live outcome — see below',
  },
  {
    dimension: 'Cardholder behavior monitoring',
    build: 'Configure your own trigger conditions against DiVA views',
    buy: 'Not yet a live outcome — see below',
  },
  {
    dimension: 'ACH fund movement (Program/User Funding)',
    build: 'Retrieve and reconcile transaction-level detail yourself',
    buy: 'Not yet a live outcome — see below',
  },
];

function ComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  return (
    <div className="border border-brand-border rounded-2xl overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[240px] font-mono text-xs uppercase">Dimension</TableHead>
            <TableHead className="font-mono text-xs uppercase text-brand-gray">Build it yourself</TableHead>
            <TableHead className="font-mono text-xs uppercase text-brand-black font-semibold">FDW</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="text-sm">
          {rows.map((row) => (
            <TableRow key={row.dimension}>
              <TableCell className="font-medium font-mono text-xs">{row.dimension}</TableCell>
              <TableCell className="text-brand-gray">{row.build}</TableCell>
              <TableCell className="font-medium text-brand-black">{row.buy}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export const CompareView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Build vs. buy — An AI-Native Alternative to Marqeta APIs"
        description="Every card programme can build anomaly detection on raw Marqeta data. FDW sells the finished outcome instead — an AI-native process checks every transaction, a human reviews what's flagged, and you get a permanent, verifiable record. No engineering investment, no pipeline to maintain."
        canonical="https://fivedollarworkday.com/compare"
      />

      <main className="relative min-h-screen bg-brand-surface text-brand-black font-sans">

        {/* ── 1. HERO ─────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-24 md:py-32 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl flex flex-col gap-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray">
                Build vs. buy
              </p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-brand-black">
                Build it on Marqeta's APIs, or buy the finished outcome.
              </h1>
              <p className="text-lg text-brand-charcoal font-light leading-relaxed">
                Either way, you're still on Marqeta's rails. The question is who does the matching,
                the reviewing, and the proving — your team, or ours.
              </p>
            </div>
          </div>
        </section>

        {/* ── 2. BLOCK A — DETECTION (live outcome) ───────────────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-10 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                  Auth/clearing anomaly detection
                </p>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand-black">
                  This one's live today.
                </h2>
              </div>
              <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">
                Live
              </Badge>
            </div>

            <ComparisonTable rows={DETECTION_ROWS} />

            <p className="text-sm text-brand-gray font-light mt-6 max-w-2xl">
              Every flag is a real statistical outlier against your own history — and every flag
              gets a human look before it's called resolved, so edge cases like tipping
              adjustments or currency drift get caught by a person, not shipped as a false alarm.
            </p>
          </div>
        </section>

        {/* ── 3. BLOCK B — DIVA-SHAPED USE CASES (exploratory) ────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-10 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-2">
                  Reporting &amp; compliance
                </p>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand-black">
                  These aren't live yet.
                </h2>
              </div>
              <Badge variant="outline" className="text-[10px] border-brand-border text-brand-gray">
                Exploratory
              </Badge>
            </div>

            <ComparisonTable rows={DIVA_ROWS} />

            <p className="text-sm text-brand-gray font-light mt-6 max-w-2xl">
              We're mapping these against Marqeta's DiVA reporting surface — BSA/AML, settlement,
              cardholder behavior, fund movement. Nothing here is built or priced yet. If one of
              these is a real problem for you, tell us — that's what shapes what gets built next.
            </p>
          </div>
        </section>

        {/* ── 4. CLOSING — AINS framing + shared CTA ──────────────── */}
        <section className="px-6 md:px-12 py-24">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-lg flex flex-col gap-6">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-black">
                You're not choosing a tool. You're choosing who owns the outcome.
              </h2>
              <p className="text-brand-charcoal font-light">
                Sign up, or message us directly — same door, whether it's the outcome that's live
                today or the one that isn't yet.
              </p>
              <ConnectActions onSignUpClick={() => navigate('/login')} />
            </div>
          </div>
        </section>

      </main>
    </>
  );
};